<script setup>
import '@/inc/module/SearchFieldMulti/searchfield_multi.scss';
import {
	useApiSearch,
	useArrowKeyNavigation,
	useGroupExpansion,
	useIataManagement,
	useKeyboardHandlers,
	useLocalStorageHistory,
	useSearchFieldMultiCore
} from './SearchFieldMulti.js';

// Props
const props = defineProps({
	label: {
		type: String,
		required: true
	},
	bound: {
		type: String,
		required: true
	},
	legIndex: {
		type: Number,
		default: 0
	},
	data: {
		type: Object,
		default: () => ({})
	},
	multiMode: {
		type: Number,
		// -1 = auto, 0 = disabled, 1 = enabled
		default: -1
	}
});

const inputElement = ref(null);
const searchFieldMulti = ref(null);
const searchFieldMultiWrapper = ref(null);
const chipScroller = ref(null);

/**
 * Initialize core functionality
 */
const coreState = useSearchFieldMultiCore(props, inputElement, searchFieldMulti, searchFieldMultiWrapper, chipScroller);
const apiState = useApiSearch(coreState, props);
const iataHelpers = useIataManagement(coreState, props);
const historyState = useLocalStorageHistory(coreState, apiState, iataHelpers);
const navigationState = useArrowKeyNavigation(coreState, apiState, historyState, iataHelpers);
const keyboardHandlers = useKeyboardHandlers(coreState, navigationState, iataHelpers);
const { toggleGroup: groupToggle, isActive: groupIsActive } = useGroupExpansion();


// Provide helpers to child components
provide('searchFieldMultiHelpers', {
	closeDropdown: coreState.closeDropdown,
	isMaxIatasAdded: coreState.isMaxIatasAdded,

	flatDisplayList: navigationState.flatDisplayList,
	hoveredIndex: navigationState.hoveredIndex,
	isItemHovered: navigationState.isItemHovered,
	setHoveredItem: navigationState.setHoveredItem,

	addItemExclusive: iataHelpers.addItemExclusive,
	isItemAdded: iataHelpers.isItemAdded,
	toggleIata: iataHelpers.toggleIata,
	toggleItem: iataHelpers.toggleItem
});

// Core state
const {
	showMissingAirportLink,
	isMultipleIataEnabled,
	isMaxIatasAdded,
	checkedIatas,
	inputValue,
	isDropdownOpen,
	focusInput,
	onInputFocus,
	onInputBlur,
	closeDropdown
} = coreState;

// API state
const {
	apiResponse,
	onInputChange,
	isSearchResultsVisible,
	isNoResultsVisible,
	popularAirports,
	isPopularAirportsVisible
} = apiState;

// IATA helpers
const {
	getTemplateKey,
	iatasHistory,
	iatasHistoryClear,
	deleteAllItems,
	toggleItem
} = iataHelpers;

const { isHistoryVisible } = historyState;

// Navigation state
const {
	nearbyCitiesFiltered,
	onArrowKey
} = navigationState;

// Keyboard handlers
const {
	onEnter,
	onBackspace,
	onTab
} = keyboardHandlers;

</script>

<template>
  <!-- eslint-disable vue/singleline-html-element-content-newline -->
  <div
    ref="searchFieldMulti"
    class="SearchFieldMulti__modal"
    :class="{
      'SearchFieldMulti__modal--open': isDropdownOpen,
    }"
    @click="focusInput"
  >
    <div
      class="SearchFieldMulti__wrapper"
      :class="{
        'SearchFieldMulti__wrapper--active': isDropdownOpen,
        'multi-mode': isMultipleIataEnabled,
      }"
    >
      <label
        for="from"
        class="SearchFieldMulti__label"
      >{{ props.label }}</label>
      <div class="SearchFieldMulti__input__wrapper">
        <!-- Selected IATAs -->
        <div
          ref="chipScroller"
          class="SearchFieldMulti__field__scroller"
          tabindex="-1"
        >
          <div class="SearchFieldMulti__checked_iatas_container">
            <div
              v-for="(item, index) in checkedIatas"
              :key="getTemplateKey(item, index)"
              class="SearchFieldMulti__checked_iata"
            >
              <span
                v-if="item.isMetro && checkedIatas.length ===1"
                class="all-airports"
              >{{ $l('module.searchField.allAirports') }},
              </span>
              <span>{{ item.text }}</span>
              <div class="SearchFieldMulti__checked_iata--clear">
                <i
                  class="icon icon-cross"
                  @click.stop="toggleItem(item, item.isMetro)"
                  @touchend.prevent="toggleItem(item, item.isMetro)"
                />
              </div>
            </div>
          </div>
          <input
            id="from"
            ref="inputElement"
            :value="inputValue"
            type="text"
            name="from"
            class="SearchFieldMulti__field SearchFieldMulti__field--from iata_field_module"
            autocomplete="off"
            tabindex="0"
            autocorrect="off"
            autocapitalize="none"
            spellcheck="false"
            enterkeyhint="next"
            @input="onInputChange"
            @focus="onInputFocus"
            @blur="onInputBlur"
            @keydown.backspace="onBackspace"
            @keydown.enter="onEnter"
            @keydown.tab="onTab"
            @keydown.arrow-up.prevent="onArrowKey('up')"
            @keydown.arrow-down.prevent="onArrowKey('down')"
          >
        </div>
        <button
          class="SearchFieldMulti__clear_btn"
          tabindex="-1"
          @click="deleteAllItems"
        >
          <i class="icon icon-cross" />
        </button>
      </div>

      <button class="SearchFieldMulti__apply_btn">{{ $l('global.simplePhrases.apply') }}</button>
    </div>

    <div
      class="SearchFieldMulti__autocomplete_container"
      :class="{
        'SearchFieldMulti__autocomplete_container--multi': isMultipleIataEnabled,
        'block': isDropdownOpen,
      }"
    >
      <div class="SearchFieldMulti__rounded_corners">
        <div
          ref="searchFieldMultiWrapper"
          class="SearchFieldMulti__scroll_wrapper"
        >
          <template v-if="isNoResultsVisible">
            <div class="SearchFieldMulti__section_header SearchFieldMulti__section_header--rounded">
              <span class="searchfield-icons-alert" />
              <span>
                {{ $l('module.searchfield.no_results') }}
              </span>
            </div>
            <div
              v-if="showMissingAirportLink"
              class="SearchFieldMulti__section_container"
              @click.stop
            >
              <a href="/information?view=missingAirport">
                <span class="SearchFieldMulti__row">
                  <span class="SearchFieldMulti__row__inner"><span>{{ $l('infopage.sidebar.reportMissingAirports') }}</span></span>
                </span>
              </a>
            </div>
          </template>
          <!-- History -->
          <template v-if="isHistoryVisible">
            <div class="SearchFieldMulti__section_header SearchFieldMulti__section_header--history SearchFieldMulti__section_header--rounded">
              <div>
                <span class="searchfield-icons-previous" />
                <span>{{ $l('searchBox.PreviousSearches') }}</span>
              </div>
              <div
                class="SearchFieldMulti__clearBtn"
                @click="iatasHistoryClear"
              >
                <span class="searchfield-icons-clear" />
              </div>
            </div>
            <div class="SearchFieldMulti__section_container">
              <SearchFieldMultiItem
                v-for="(item, index) in iatasHistory?.[props.bound]"
                :key="getTemplateKey(item, index)"
                :item="item"
              />
            </div>
          </template>

          <!-- Airports -->
          <template v-if="isSearchResultsVisible && apiResponse.airports.length && !isHistoryVisible">
            <div
              v-if="apiResponse.usedFuzzySearch"
              class="SearchFieldMulti__section_header"
            >
              <span class="searchfield-icons-alert" />
              <span>{{ $l('module.searchfield.other_possible_results') }}</span>
            </div>
            <div
              v-else
              class="SearchFieldMulti__section_header"
            >
              <span>{{ $l('module.searchfield.cities_and_airports') }}</span>
            </div>

            <div class="SearchFieldMulti__section_container">
              <SearchFieldMultiItem
                v-for="(item, index) in apiResponse.airports"
                :key="getTemplateKey(item, index)"
                :item="item"
              />
            </div>
          </template>

          <!-- Countries and Regions -->
          <template v-if="isSearchResultsVisible && apiResponse.countries.length && !isHistoryVisible">
            <div class="SearchFieldMulti__section_header">
              <span>{{ $l('module.searchfield.countries') }}</span>
            </div>
            <template
              v-for="(country) in apiResponse.countries"
              :key="country.country_iso2"
            >
              <div class="SearchFieldMulti__section_container">
                <div
                  class="SearchFieldMulti__group_header"
                  :class="{ expanded: groupIsActive(country.country_iso2) }"
                  @click="groupToggle(country.country_iso2)"
                >
                  <div>
                    <img
                      :src="`/assets/images/searchfield/country-flags/${country.country_iso2}.png`"
                    >
                    <span>{{ country.name }}</span>
                  </div>
                  <span class="searchfield-icons-arrowdown" />
                </div>
                <div
                  v-if="groupIsActive(country.country_iso2)"
                  class="SearchFieldMulti__group_container"
                >
                  <SearchFieldMultiItem
                    v-for="(item, index) in country.children"
                    :key="getTemplateKey(item, index)"
                    :item="item"
                  />
                </div>
              </div>
            </template>
          </template>

          <!-- Nearby Cities -->
          <template v-if="isSearchResultsVisible && nearbyCitiesFiltered.length && !isHistoryVisible">
            <div class="SearchFieldMulti__section_header">
              <span>{{ $l('module.searchfield.are_u_looking') }}</span>
            </div>

            <div class="SearchFieldMulti__section_container visible">
              <SearchFieldMultiItem
                v-for="(item, index) in nearbyCitiesFiltered"
                :key="getTemplateKey(item, index)"
                :item="item"
              />
            </div>
          </template>

          <!-- Popular Airports -->
          <template v-if="isPopularAirportsVisible && !isHistoryVisible">
            <div class="SearchFieldMulti__section_header">
              <span>{{ $l('module.searchfield.most_popular') }}</span>
            </div>
            <div class="SearchFieldMulti__section_container visible">
              <SearchFieldMultiItem
                v-for="(item, index) in popularAirports"
                :key="getTemplateKey(item, index)"
                :item="item"
              />
            </div>
          </template>
        </div>
      </div>

      <div
        v-show="!isNoResultsVisible"
        class="SearchFieldMulti__apply_container SearchFieldMulti__apply_container--visible"
      >
        <div>
          <div
            class="limit-warning-msg"
            :class="{
              'visible': isMaxIatasAdded
            }"
          >
            {{ $l('module.searchfield.limit_warning_msg') }}
          </div>
        </div>

        <div class="SearchFieldMulti__apply_container__actions">
          <button
            class="SearchFieldMulti__cancel_btn abBtn"
            @click.stop="closeDropdown"
          >
            {{ $l('global.simplePhrases.abort') }}
          </button>
          <button
            class="SearchFieldMulti__apply_btn abBtn abBtn--yellowBlue m-l-5"
            @click.stop="closeDropdown"
          >
            {{ $l('global.simplePhrases.apply') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
